import { createSelector } from 'reselect';
import { fromPairs, isEmpty, keyBy } from 'lodash-es';
import type { Signal } from '@bayer-int/portletce-shared';
import { zip, nullableToArray, isNotNullable } from '@bayer-int/portletce-shared';

import type {
    FiltersPopupDefinition,
    InteractiveTitleSettingDefinition,
    PortletDebugInformation,
} from 'header/types';
import type { AcSpecIdWithScopedProperties, ChartRenderProps } from 'portlet/models';
import {
    Specification,
    View as ViewModel,
    FilterData,
    FilterDataArray,
    SettingDefinition,
} from 'portlet/models';
import type { TitleAlign } from 'portlet/models/settings/settingDefinitions/titleAlignSetting';
import { AsyncData } from 'portlet/common/models/asyncData';
import type { AppError } from 'portlet/common/errors';
import { concatUniqByProp } from 'portlet/common/utils';
import { createDeepEqualSelector } from 'common/utils';
import { VegaSpecificationService } from 'portlet/services';

import type { AppState } from './reducer';
import * as FiltersSettings from './filters-settings';
import * as EmailSupport from './email-support';
import * as View from './view';
import * as Init from './init';
import * as Auth from './auth';
import * as Performance from './performance';

const mergeSignals = concatUniqByProp('name');

export type AppSelector<T> = (state: AppState) => T;

const specificationSelector = Init.Selectors.specificationSelector;
const configSelector = Init.Selectors.configSelector;
const initErrorSelector = Init.Selectors.initErrorSelector;
const filterSelector = FiltersSettings.Selectors.filterSelector;
const filterSignalSelector = FiltersSettings.Selectors.toSignals;
const currentViewSelector = FiltersSettings.Selectors.currentView;
const currentViewDataSelector = View.Selectors.viewDataSelector;
const cacheInvalidationTimestamp = View.Selectors.cacheInvalidationTimestamp;
const isSuspendedSelector = View.Selectors.isSuspendedSelector;
const settingValuesSelector = FiltersSettings.Selectors.settingsWithFilters;
const settingStackSelector = FiltersSettings.Selectors.settingStackSelector;
const settingsDefinitionsSelector = FiltersSettings.Selectors.settingsFormDefinition;
const settingsFormDefinitionSelector = FiltersSettings.Selectors.settingsFormDefinitions;
const observedFiltersSelector = FiltersSettings.Selectors.observedFilters;
const areSettingsConsistentSelector = FiltersSettings.Selectors.areSettingsConsistent;
const queryLinkSelector = FiltersSettings.Selectors.queryLinkSelector;
const displayedShortcuts = FiltersSettings.Selectors.displayedShortcuts;
const supportEmailStateSelector = EmailSupport.Selectors.supportEmailStateSelector;
const queryMetadataSelector = FiltersSettings.Selectors.queryMetadataSelector;
const userSelector = Auth.Selectors.userSelector;
const metadataSelector = Init.Selectors.metadataSelector;
const performanceStateSelector = Performance.Selectors.performanceStateSelector;
const transitionsSelector = Performance.Selectors.transitionsSelector;
const isPerformanceLowSelector = Performance.Selectors.isPerformanceLowSelector;
const resolvedSignalsSelector = Init.Selectors.resolvedSignalsSelector;

const portletTitleSelector: AppSelector<string> = createSelector(
    settingValuesSelector,
    Specification.getTitle,
);

const portletSubtitleSelector: AppSelector<string> = createSelector(
    settingValuesSelector,
    Specification.getSubtitle,
);

const portletShortcutsColorSelector: AppSelector<string> = createSelector(
    settingValuesSelector,
    Specification.getShortcutsColor,
);

const portletViewListSelector: AppSelector<Array<{ id: number; label: string }>> = createSelector(
    specificationSelector,
    spec => (spec ? Specification.getListOfViews(spec) : []),
);

const portletTitleAlignSelector: AppSelector<TitleAlign> = createSelector(
    settingValuesSelector,
    Specification.getTitleAlign,
);

const chartConfigSelector: AppSelector<ChartRenderProps | null> = createSelector(
    configSelector,
    currentViewSelector,
    currentViewDataSelector,
    cacheInvalidationTimestamp,
    filterSignalSelector,
    FiltersSettings.Selectors.currentViewSignals,
    isSuspendedSelector,
    specificationSelector,
    (
        config,
        currentView,
        currentData,
        timestamp,
        filterAndSettingSignals,
        viewSignals,
        isSuspended,
    ): ChartRenderProps | null => {
        if (!currentView || !currentData) return null;

        const signals = mergeSignals(filterAndSettingSignals, viewSignals);
        const repeats = currentView.repeats || [];

        const sourceSpecs = zip(
            nullableToArray(currentView.datapipe.source),
            nullableToArray(currentData.source),
        )
            .map(([definition, spec]) =>
                VegaSpecificationService.renderAclibSpec(definition, spec, signals, repeats),
            )
            .filter(spec => spec !== null);

        const documentSpec = VegaSpecificationService.renderAclibSpec(
            currentView.datapipe.document,
            currentData.document,
            signals,
            repeats,
        );
        const documentProps =
            typeof currentView.datapipe.document !== 'string'
                ? currentView.datapipe.document.props
                : null;

        const transformFromDocument = documentSpec.source.usermeta.transform || null;

        const definitionsExtended = [
            ...currentView.datapipe.transforms,
            isNotNullable(documentProps)
                ? ({ name: 'fromDoc', props: documentProps } as AcSpecIdWithScopedProperties)
                : null,
        ].filter(isNotNullable);
        const specsExtended = [...currentData.transforms, transformFromDocument].filter(
            isNotNullable,
        );

        const zippedTransforms =
            transformFromDocument && typeof transformFromDocument !== 'string'
                ? zip(definitionsExtended, specsExtended)
                : zip(currentView.datapipe.transforms, currentData.transforms);

        const transformSpecs = zippedTransforms
            .map(([definition, spec]) =>
                VegaSpecificationService.renderAclibSpec(definition, spec, signals, repeats),
            )
            .filter(spec => spec !== null);

        const utilitySpecs = zip(currentView.utilities || [], currentData.utilities).map(
            ([definition, spec]) =>
                VegaSpecificationService.renderAclibSpec(definition, spec, signals, repeats),
        );

        const specs = [...sourceSpecs, ...transformSpecs];

        // We need to remove `transform` from document's usermeta, because it will try to be resolved second time in PreProcessor.ts.
        // Everything when comes to transform is handled here, because we are passing it to DFE, just as transform.
        const { usermeta, ...document } = currentData.document;
        const { transform, ...restUsermeta } = usermeta;
        const newDocument = { ...document, usermeta: restUsermeta };
        const disableAnimations = !!(config && config.disablechartanimations);

        return {
            viewId: currentView.id,
            specs,
            filterKeyToEmitFrom: null,
            utilities: utilitySpecs,
            document: newDocument,
            documentSignals: VegaSpecificationService.getSignalsForSpec(
                currentView.datapipe.document,
                signals,
            ),
            documentImportMapping: VegaSpecificationService.getDocumentImportMapping(
                currentView.datapipe.document,
            ),
            documentRepeats: repeats,
            cacheInvalidationTimestamp: timestamp,
            isSuspended,
            disableAnimations,
        };
    },
);

const isChartDataUpToDateSelector: AppSelector<boolean> = createSelector(
    currentViewSelector,
    currentViewDataSelector,
    (currentView, currentData) => {
        if (!currentView || !currentData) return true;

        return currentView.id === currentData.viewId;
    },
);

const isDataLoadingSelector: AppSelector<boolean> = state =>
    AsyncData.is.PENDING(state.view.currentViewData);

const isDataReloadingSelector: AppSelector<boolean> = state =>
    AsyncData.is.RELOADING(state.view.currentViewData);

const dataErrorSelector: AppSelector<AppError | null> = state =>
    AsyncData.match(state.view.currentViewData, { ERROR: err => err });

const dataFromSelector: AppSelector<{ date: Date; isDataPossiblyCached: boolean } | null> = state =>
    state.filtersSettings.dataFrom;

const filtersFormDefinitionSelector: AppSelector<FiltersPopupDefinition['sections']> =
    createDeepEqualSelector(
        currentViewSelector,
        filterSelector,
        dataFromSelector,
        (currentView, filters, dataFrom) => {
            const defaultSections = {
                dateAge: {
                    title: '',
                    date: '',
                    color: '',
                },
                defaultFilters: [],
                localFilters: [],
                notSetFilters: [],
                userSelectedFilters: [],
                dataFrom: null,
            };

            if (!currentView) return defaultSections;

            const defaultFilters = ViewModel.getDefaultFilters(currentView).map(
                FilterData.toFilterDefinition,
            );
            const currentFilters = filters.map(FilterData.toFilterDefinition);

            return {
                ...defaultSections,
                defaultFilters,
                localFilters: currentFilters,
                dataFrom,
            };
        },
    );

const portletDebugInfoSelector: AppSelector<PortletDebugInformation> = state => {
    const portletConfig = configSelector(state);
    const filters = filterSelector(state);
    const queryMetadata = queryMetadataSelector(state);
    const metadata = metadataSelector(state);
    const settings = keyBy(settingsFormDefinitionSelector(state), 'key');
    return {
        userInfo: {
            label: 'User info',
            key: 'user_info',
            value: {
                user: userSelector(state)?.cwid || userSelector(state)?.id || '',
            },
        },
        portletMetadata: {
            label: 'Metadata',
            key: 'metadata',
            value: metadata,
        },
        portletInfo: {
            label: 'Config',
            key: 'portlet_config',
            value: portletConfig,
        },
        query: {
            label: 'Query',
            key: 'query',
            value: {
                type: queryMetadata?.type,
                metadata: queryMetadata,
            },
        },
        userFilterInformation: {
            key: 'filters',
            label: 'Filters',
            value: fromPairs(filters.map(filter => [filter.item, filter.value])),
        },
        user: {
            label: 'Settings',
            key: 'settings',
            value: settings,
        },
    };
};

const isDataConsistent: AppSelector<boolean> = createSelector(
    isChartDataUpToDateSelector,
    areSettingsConsistentSelector,
    (chartDataUpToDate, settingsConsistent) => chartDataUpToDate && settingsConsistent,
);

const showSupportFormSelector: AppSelector<boolean> = createSelector(
    specificationSelector,
    spec => !!spec?.supportContentArea,
);

const infoUrlSelector: AppSelector<string | null> = createSelector(
    specificationSelector,
    spec => spec?.infoUrl ?? null,
);

const replacementTagsSelector: AppSelector<Record<string, string>> = createSelector(
    settingsDefinitionsSelector,
    filterSelector,
    resolvedSignalsSelector,
    (settings, filters, signals) => ({
        ...ViewModel.toReplacementTags(signals),
        ...FilterDataArray.toReplacementTags(filters),
        ...SettingDefinition.toReplacementTags(settings),
    }),
);
const interactiveTitleSelector: AppSelector<Record<string, InteractiveTitleSettingDefinition>> =
    createSelector(settingsDefinitionsSelector, SettingDefinition.toInteractiveReplacementTags);

const dataToDownloadSelector: AppSelector<Signal | null> = createSelector(
    resolvedSignalsSelector,
    currentViewSelector,
    (signals, currentView) => {
        const signalToDownload = currentView && ViewModel.getSignalToDownaload(currentView);
        if (!signalToDownload) return null;
        const signalToDownloadName =
            typeof signalToDownload === 'string' ? signalToDownload : signalToDownload?.signalName;
        const signal = signals.find(signal => signal.name === signalToDownloadName);

        return signal?.value ? signal : null;
    },
);

const dataToDownloadFormatSelector: AppSelector<'csv' | 'xlsx'> = createSelector(
    currentViewSelector,
    currentView => {
        const signalToDownload = currentView && ViewModel.getSignalToDownaload(currentView);
        if (!signalToDownload) return 'xlsx';

        return typeof signalToDownload === 'string' ? 'xlsx' : signalToDownload.downloadFormat;
    },
);

const customErrorMessagesSelector: AppSelector<Record<
    string,
    { title: string; message: string }
> | null> = createSelector(currentViewSelector, specificationSelector, (currentView, spec) => {
    const customErrorMessagesSpec = spec && Specification.getCustomErrorMessages(spec);
    const customErrorMessagesView = currentView && ViewModel.getCustomErrorMessages(currentView);
    return { ...customErrorMessagesSpec, ...customErrorMessagesView };
});

export const Selectors = {
    configSelector,
    portletTitleSelector,
    portletSubtitleSelector,
    portletShortcutsColorSelector,
    portletTitleAlignSelector,
    portletViewListSelector,
    currentViewSelector,
    chartConfigSelector,
    isDataLoadingSelector,
    isDataReloadingSelector,
    isChartDataUpToDateSelector,
    dataErrorSelector,
    settingsFormDefinitionSelector,
    settingsDefinitionsSelector,
    settingStackSelector,
    settingValuesSelector,
    initErrorSelector,
    filtersFormDefinitionSelector,
    filterSelector,
    observedFiltersSelector,
    areSettingsConsistentSelector,
    isDataConsistent,
    queryLinkSelector,
    portletDebugInfoSelector,
    supportEmailStateSelector,
    userSelector,
    showSupportFormSelector,
    replacementTagsSelector,
    interactiveTitleSelector,
    infoUrlSelector,
    specificationSelector,
    displayedShortcuts,
    performanceStateSelector,
    transitionsSelector,
    isPerformanceLowSelector,
    dataToDownloadSelector,
    dataToDownloadFormatSelector,
    customErrorMessagesSelector,
};
